import ic_dropdown_down from '@material-design-icons/svg/outlined/arrow_drop_down.svg?raw';
import ic_close from '@material-design-icons/svg/outlined/close.svg?raw';
import ic_sync from '@material-design-icons/svg/outlined/sync.svg?raw';
import { html, LitElement, TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";
import { _APP_DATA } from '../../../../data/AppData';
import { IGroup, IGrupoInfo } from '../../../../data/models/Entities';
import { MEscuela } from '../../../../data/models/MEscuela';
import { EEstadoProximidad, MProximidadTutorItem } from "../../../../data/models/MProximidadItem";
import { _DICT_GRUPO_ALUMNOS_MAP, _SvAlumnoObtenerListaByGrupo } from '../../../../data/services/Alumno';
import { _DICT_ESCUELAS, _SvEscuelaObtenerConfiguracionByEscuela } from '../../../../data/services/Escuela';
import { _GRUPOSINFO_MAP } from '../../../../data/services/Grupo';
import "../../../../ui/components-lit/BubbleModal/BubbleModal";
import "../../../../ui/components-lit/Buttons/IconButton/IconButton";
import "../../../../ui/components-lit/Icon/Icon";
import "../../../../ui/components-lit/InfiniteProgress/InfiniteProgress";
import { AppPopoverComponent } from '../../../../ui/components-lit/Popover/Popover';
import "../../../../ui/components-lit/RelativeTime/RelativeTime";
import _L from "../../../../utils/Labels";
import { MEntregaItem } from '../../models/MEntregaItem';
import { MGroupP } from '../../models/MGroupP';
import "../EntregaItem/EntregaItem";
import { ProximidadGroupFilter } from '../ProximidadGroupFilter/ProximidadGroupFilter';
import "./ProximidadPopoverContent.scss";

const ORDER_ESTADO: EEstadoProximidad[] = [
    EEstadoProximidad.LLEGADA,
    EEstadoProximidad.EN_ZONA_ESCOLAR,
    EEstadoProximidad.EN_CAMINO,
    EEstadoProximidad.ABANDONO_ZONA,
    EEstadoProximidad.CANCELADO,
    EEstadoProximidad.COMPLETADO
];

export type TProximGroupID = IGroup | null | number;

@customElement('app-proximidad-popover-content')
export class ProximidadPopoverContent extends LitElement {
    @property({ type: Object }) groupedData: Map<EEstadoProximidad, MEntregaItem[]> = new Map();
    @property({ type: Array }) availableGroups: MGroupP[] | null = null;
    @property({ type: Array }) selectedGroup: TProximGroupID[] = [];

    @state() private isConsulting: boolean = false;

    @query('#btn-group-filter') private btnGroupFilter!: HTMLElement | null;

    private groupPopover: AppPopoverComponent = new AppPopoverComponent();
    private groupFilter: ProximidadGroupFilter = new ProximidadGroupFilter();

    private consultDelegate: ((idGrupo: number, idEscuela: number) => Promise<MProximidadTutorItem[]>) | null = null;
    private __consultPromise: Promise<void> | null = null;
    private __savedDataByGroup: Map<number, MProximidadTutorItem[]>;

    constructor() {
        super();
        this.__savedDataByGroup = new Map<number, MProximidadTutorItem[]>();
        this.groupPopover.classList.add('group-list-popover');
        this.groupPopover.placement = "bottom-start";
        this.groupFilter.onSelectGroup = (idGrupo) => this.onSelectGroup(idGrupo);
        this.groupPopover.appendChild(this.groupFilter);
    }

    public parentPopover: AppPopoverComponent | null = null;

    public get aproximateHeight(): number {
        if (!this.groupedData || this.groupedData.size === 0) {
            return 180;
        }
        const totalItems = Array.from(this.groupedData.values()).reduce((acc, items) => acc + items.length, 0);
        return Math.max(totalItems * 135 + 40, 180);
    }

    public set setConsultDelegate(delegate: (idGrupo: number, idEscuela: number) => Promise<MProximidadTutorItem[]>) {
        this.consultDelegate = delegate;
    }

    public syncData(especificGroupIds?: number[]): Promise<void> {
        return this._syncData(false, especificGroupIds);
    }

    protected createRenderRoot(): HTMLElement | DocumentFragment {
        return this;
    }

    // ***** PRIVATE METHODS *****

    protected render(): TemplateResult {
        return html`
            ${this.renderHeader()}
            ${this.renderContent()}
        `;
    }

    private renderHeader() {
        const hasAvailableGroups = this.availableGroups?.length > 0;
        const grs = this.getResolvedSelectedGroups();
        const hasSelection = grs.length > 0;
        const groupLabel = grs.length === 0
            ? _L("ui.proximidad.grupo")
            : grs.length === 1
                ? grs[0].NombreGrupo
                : _L("ui.proximidad.grupos_seleccionados", String(grs.length));

        return html`
        <div class="modal-header">
            <div class="title">
                <b>${_L("ui.proximidad.title")}</b>
                <pre>${_L("ui.proximidad.subtitle")}</pre>
            </div>
            <div class="actions">

                ${hasAvailableGroups || hasSelection
                ? html`
                    ${hasAvailableGroups
                        ? html`
                        <div id="btn-group-filter" class=${"filter-toggle" + (hasSelection ? " active" : "") + (this.isConsulting ? " disabled" : "")} @click=${() => this.toggleGroupPopover()}>
                            <span>${groupLabel}</span>
                            <div class="icon-wrapper">
                                <app-icon-dumb slot="icon" style='height: 20px; width: 20px;'>${unsafeSVG(ic_dropdown_down)}</app-icon-dumb>
                            </div>
                        </div>`
                        : html`<span class="active">${groupLabel}</span>`
                    }
                `
                : html``}

                <app-icon-button
                    k_size="small"
                    k_label=${_L("general.sync")}
                    k_colorStyle="standard"
                    style="--btn-theme-color: var(--color-blue-action);"
                    class="refresh-icon ${this.isConsulting ? "loading" : ""} ${!this.consultDelegate ? "hide" : ""}"
                    @click="${() => this._syncData()}">
                        <app-icon-dumb slot="icon" style='height: 20px; width: 20px;'>${unsafeSVG(ic_sync)}</app-icon-dumb>
                </app-icon-button>

                <app-icon-button
                    k_size="small"
                    k_label=${_L("general.close")}
                    k_colorStyle="standard"
                    style="--btn-theme-color_: var(--color-blue-action);"
                    class="${this.parentPopover ? "" : "hide"}"
                    @click="${() => setTimeout(() => this.parentPopover?.hide(), 400)}">
                        <app-icon-dumb slot="icon" style='height: 20px; width: 20px;'>${unsafeSVG(ic_close)}</app-icon-dumb>
                </app-icon-button>
            </div>
            ${this.isConsulting
                ? html`<app-infinite-progress></app-infinite-progress>`
                : ""}
        </div>
        `;
    }

    private renderContent() {
        return html`
        <div class="modal-content" class="shy_scrollthin">
            ${this.groupedData.size === 0 || !this.selectedGroup?.length
                ? this.renderContentEmpty()
                : this.renderContentGroupedItems()
            }
        </div>
        `;
    }

    /** Sincroniza el componente de selección de grupos con el estado actual del popover. */
    private updateGroupFilter() {
        const currentG = this.getCurrentGrupoGlobal();
        this.groupFilter.availableGroups = this.availableGroups || [];
        this.groupFilter.selectedGroupIds = this.getResolvedSelectedGroups().map(g => g.IdGrupo);
        this.groupFilter.currentGroupId = currentG?.IdGrupo ?? null;
    }

    private renderContentEmpty() {
        if (this.groupedData?.size > 0 && !!this.selectedGroup?.length) {
            return html``;
        }
        const grs = this.getResolvedSelectedGroups();
        const escuela = Array.from(this.getEscuelasForSelectedGroups());
        const message = this.isConsulting
            ? _L("general.syncing")
            : !grs.length
                ? _L("ui.proximidad.seleccionar_grupo")
                : escuela.every(e => !e.getKidiRadarActivo())
                    ? _L("ui.proximidad.radar_desactivado", escuela.map(d => d.Nombre || "").join(", "))
                    : _L("ui.proximidad.tutores_no_en_camino");

        return html`<div class="no-data">${message}</div>`;
    }

    private renderContentGroupedItems() {
        return Array.from(this.groupedData.values()).map((statusGroup) => {
            const d = statusGroup[0].data[0];
            return html`
            <div class="group-items" style="--color-proximidad: ${d.getColorForEstado()};">
                <div class="status-group-header">
                    ${d.getEstadoString()}
                </div>
                ${statusGroup.map((item: MEntregaItem) => html`
                <app-proximidad-entrega-item .data=${item}>
                </app-proximidad-entrega-item>
                `)}
            </div>
            `;
        });
    }

    // ============================
    // EVENT HANDLERS
    // ============================

    /** Delegado invocado por {@link ProximidadGroupFilter} tras estabilizarse la selección: alterna un grupo puntual. */
    private onSelectGroup(g: TProximGroupID) {
        const resolvedG = this.resolveGroup(g);
        if (!resolvedG) return;

        const currentSelection = this.selectedGroup || [];
        const existingIndex = currentSelection.findIndex(sg => this.resolveGroup(sg)?.IdGrupo === resolvedG.IdGrupo);

        this.selectedGroup = existingIndex !== -1
            ? currentSelection.filter((_sg, idx) => idx !== existingIndex)
            : [...currentSelection, g];

        this.groupFilter.selectedGroupIds = this.getResolvedSelectedGroups().map(sg => sg.IdGrupo);
        this._syncData(true);
    }

    private toggleGroupPopover(open?: boolean) {
        const newState = open === undefined ? !this.groupPopover.open : open;

        if (newState) {
            this.updateGroupFilter();
            this.groupPopover.show(this.btnGroupFilter);
        } else {
            this.groupPopover.hide();
        }
    }

    // ============================
    // DATA METHODS
    // ============================

    private getEscuelasForSelectedGroups(): Set<MEscuela> {
        const escuelas = new Set<MEscuela>();
        for (const g of this.getResolvedSelectedGroups()) {
            const esc = _DICT_ESCUELAS.get(g.IdEscuela);
            if (esc) {
                escuelas.add(esc);
            }
        }
        return escuelas;
    }

    private resolveGroup(g: TProximGroupID): MGroupP | null {
        if (g instanceof Object) {
            return g;
        } else if (typeof g === "number" && _GRUPOSINFO_MAP.has(g)) {
            return _GRUPOSINFO_MAP.get(g) || null;
        }
        return null;
    }

    public getResolvedSelectedGroups(): MGroupP[] {
        if (!this.selectedGroup?.length) return [];
        return this.selectedGroup
            .map(g => this.resolveGroup(g))
            .filter((g): g is MGroupP => !!g);
    }

    /** Indica si el grupo dado (por IdGrupo resuelto) ya forma parte de la selección actual. */
    public isGroupSelected(g: TProximGroupID): boolean {
        const resolvedG = this.resolveGroup(g);
        if (!resolvedG) return false;
        return this.getResolvedSelectedGroups().some(sg => sg.IdGrupo === resolvedG.IdGrupo);
    }

    /** Agrega un grupo a la selección actual (si no estaba ya) y sincroniza filtro y datos, igual que {@link onSelectGroup}. */
    public addSelectedGroup(g: TProximGroupID): void {
        if (this.isGroupSelected(g)) return;

        this.selectedGroup = [...(this.selectedGroup || []), g];
        this.groupFilter.selectedGroupIds = this.getResolvedSelectedGroups().map(sg => sg.IdGrupo);
        this._syncData(true);
    }

    private getCurrentGrupoGlobal(): IGrupoInfo | null {
        const currentGrupoId = _APP_DATA.userData.IdGrupo;
        return _GRUPOSINFO_MAP.get(currentGrupoId) || null;
    }

    /**
     * Sincroniza los datos de proximidad para los grupos seleccionados, o para un subconjunto de grupos si se especifica.
     * @param fetchAlumnos Indica si se deben obtener los alumnos asignados a los grupos seleccionados antes de consultar la proximidad.
     * @param especificGroupIds Grupos específicos para los cuales se desea sincronizar la información. Si no se proporciona, se sincronizarán todos los grupos seleccionados.
     * @returns Una promesa que se resuelve cuando la sincronización ha finalizado.
     */
    private _syncData(fetchAlumnos = false, especificGroupIds?: number[]): Promise<void> {
        if (!this.consultDelegate || !this.selectedGroup?.length) {
            return Promise.resolve();
        }
        if (this.__consultPromise) {
            return Promise.resolve(this.__consultPromise);
        }
        this.__consultPromise = new Promise((resolve, reject) => {
            this.isConsulting = true;
            setTimeout(async () => {
                this.__consultPromise = null;
                const grs = !especificGroupIds
                    ? this.getResolvedSelectedGroups()
                    : this.getResolvedSelectedGroups().filter(g => especificGroupIds.includes(g.IdGrupo));

                if (!grs.length) {
                    setTimeout(() => { this.isConsulting = false; }, 400);
                    return resolve();
                }

                if (fetchAlumnos) for (const gr of grs) {
                    if (!_DICT_GRUPO_ALUMNOS_MAP.get(gr.IdGrupo)?.size) {
                        await _SvAlumnoObtenerListaByGrupo(gr.IdGrupo, null);
                    }
                }
                if (this.availableGroups?.length) {
                    for (const g of this.availableGroups) {
                        if (!_DICT_ESCUELAS.has(g.IdEscuela)) {
                            await _SvEscuelaObtenerConfiguracionByEscuela(g.IdEscuela);
                        }
                    }
                }

                this.isConsulting = true;

                const consultPromises = grs.map(gr => this.consultDelegate(gr.IdGrupo, gr.IdEscuela).then(dataGr => {
                    this.__savedDataByGroup.set(gr.IdGrupo, dataGr);
                    return dataGr;
                }));

                Promise.all(consultPromises)
                    .then(results => {
                        this.prepareData(results.flat(), grs);
                        setTimeout(() => { this.isConsulting = false; }, 400);
                        resolve();
                    }).catch(() => {
                        this.isConsulting = false;
                        reject();
                    });
            }, 800);
        });
        return this.__consultPromise;
    }

    /**
     * Prepara los datos de proximidad para renderizar, agrupando por estado y por alumnos asignados en clase.
     * @param dataRes Los datos consultados de proximidad de los tutores.
     * @param groupsReq Los grupos solicitados en la consulta.
     */
    private prepareData(dataRes: MProximidadTutorItem[], groupsReq: MGroupP[]) {
        const showGrupo = (this.selectedGroup?.length || 0) > 1;
        const entregaItems: Map<string, MEntregaItem> = new Map<string, MEntregaItem>();
        const idsGruposFiltro = this.getResolvedSelectedGroups().map(g => g.IdGrupo);
        const newGroupedData: Map<EEstadoProximidad, MEntregaItem[]> = new Map<EEstadoProximidad, MEntregaItem[]>();

        // Agregar los datos de los grupos que no fueron consultados, pero que están en la selección actual, para mantener la consistencia de la vista.
        const missingData = Array.from(this.__savedDataByGroup.entries())
            .filter(([idGrupo, _]) => idsGruposFiltro.includes(idGrupo) && !groupsReq.some(g => g.IdGrupo === idGrupo))
            .flatMap(([_, data]) => data);

        dataRes
            .concat(missingData)
            // ordenar por estado y luego por distancia para que los tutores más cercanos se muestren primero
            .sort((a, b) => ORDER_ESTADO.indexOf(a.Estado) - ORDER_ESTADO.indexOf(b.Estado) || a.DistanciaMetros - b.DistanciaMetros)
            .forEach((item: MProximidadTutorItem) => {
                item.filtrarAlumnosEnClasePorGrupo(idsGruposFiltro);

                if (!item.AlumnosFiltrados || item.AlumnosFiltrados.length === 0) {
                    console.debug(`ProximidadPopoverContent: ignora item de tutor ${item.IdTutor} (estado: ${item.Estado}) -> no tiene alumnos filtrados en clase para los grupos seleccionados (${idsGruposFiltro})`);
                    return;
                }

                const estadoKey = item.Estado;
                const alumnoKey = item.AlumnosFiltrados.map(d => d.IdChild).sort().join("-");
                const groupKey = `${estadoKey}-${alumnoKey}`;

                if (!entregaItems.has(groupKey)) {
                    entregaItems.set(groupKey, new MEntregaItem([], showGrupo));
                }
                entregaItems.get(groupKey).setProximidadItems(item);

                if (!newGroupedData.has(estadoKey)) {
                    newGroupedData.set(estadoKey, []);
                }
                if (!newGroupedData.get(estadoKey).includes(entregaItems.get(groupKey))) {
                    newGroupedData.get(estadoKey).push(entregaItems.get(groupKey));
                }
            });

        this.groupedData = newGroupedData;
    }
}
